// ═══ Strafor Montaj Sistemi ═══
// Standart kütüklerden istenen boyutta parça OLUŞTURMA
// Kural: X,Y kesim serbest | Z'de farklı kalınlıkta kütük kullanılır (kesim yok)

export interface IStraforKutuk {
  id: string;
  isim: string;
  en: number;       // X (mm) - tüm standartlar 1230
  boy: number;      // Y (mm) - tüm standartlar 2000
  yukseklik: number; // Z (mm) - kalınlık: 100,150,200,250,300,350
  adet: number;
  renk?: string;
  birimFiyat?: number;
  gecici?: boolean;  // Geçici/özel kütük
}

export interface IHedefParca {
  en: number;
  boy: number;
  yukseklik: number;
}

export interface IYapistamaYuz {
  eksen: 'x' | 'y' | 'z';
  yon: '+' | '-';
  alan: number; // mm²
}

// Bir kalınlık katmanı (Z ekseni)
export interface IKalinlikKatman {
  kutukId: string;
  kutukIsim: string;
  kalinlik: number;       // standart kalınlık (mm)
  gercekKalinlik: number; // kullanılan kalınlık (=kalinlik, Z'de kesim yok)
  kesilecekMi: boolean;   // Z'de her zaman false
  kesimMiktari: number;
  zPozisyon: number;      // katmanın Z konumu (mm)
  birimFiyat: number;
  renk: string;
}

// Tek blok yerleştirme
export interface IBlokYerlestirme {
  id: string;
  katmanIdx: number;
  gridPoz: { ix: number; iy: number; iz: number };
  pozisyon: { x: number; y: number; z: number };
  tamBoyut: { en: number; boy: number; yukseklik: number };
  gercekBoyut: { en: number; boy: number; yukseklik: number };
  kesilecekMi: boolean;
  kesimMiktari: { en: number; boy: number; yukseklik: number };
  yapistamaYuzleri: IYapistamaYuz[];
  katmanRenk: string;
  geciciKutukId?: string;
  geciciKutukIsim?: string;
}

// Montaj planı (bir seçenek)
export interface IMontajPlani {
  hedef: IHedefParca;
  dizilim: 'normal' | 'dondurulmus';
  kutukEn: number;
  kutukBoy: number;
  adetX: number;
  adetY: number;
  katmanlar: IKalinlikKatman[];
  bloklar: IBlokYerlestirme[];
  toplamBlok: number;
  fireOrani: number;
  hedefHacim: number;
  kullanilanHacim: number;
  fireHacim: number;
  toplamMaliyet: number;
  toplamKesimSayisi: number;
  tamBlokSayisi: number;
  toplamYapistamaAlani: number;
  kalinlikAciklama: string;
  skor: number;
  kullanilanGeciciAdet: number;
  geciciTasarruf: number;
}

// Kalınlık renk paleti
export const KALINLIK_RENKLERI: Record<number, string> = {
  50:  '#A5D6A7',
  100: '#FFB74D',
  150: '#4FC3F7',
  200: '#81C784',
  250: '#F06292',
  300: '#9575CD',
  350: '#FFD54F',
};

// ═══ Kesim Animasyonu Interface'leri ═══

export type KesimTipi = 'sicak-tel' | 'duzlem' | 'serbest-form';
export type DetaySeviyesi = 'basit' | 'orta' | 'detayli';
export type AnimasyonDurumu = 'bekliyor' | 'oynuyor' | 'duraklatildi' | 'tamamlandi';

export interface IKesimParams {
  blokId: string;
  eksen: 'x' | 'y';
  kesimMiktari: number;         // mm
  tamBoyut: { en: number; boy: number; yukseklik: number };
}

export interface IKesimAdimi {
  sira: number;
  blok: IBlokYerlestirme;
  params: IKesimParams;
}

export interface IArtikParca {
  id: string;
  kaynak: string;              // Hangi bloktan kesildi
  boyut: { en: number; boy: number; yukseklik: number };
  agirlik: number;             // kg
  mesh?: any;                  // THREE.Mesh ref (runtime)
  rafPozisyon?: number;
}

export interface IKesimAnimasyonState {
  aktif: boolean;
  durum: AnimasyonDurumu;
  kesimTipi: KesimTipi;
  detaySeviyesi: DetaySeviyesi;
  hizCarpani: number;
  mevcutAdim: number;
  toplamAdim: number;
  ilerleme: number;            // 0-100
  adimModu: boolean;
}

// EPS yoğunluğu (kg/m³) - artık ağırlık hesabı için
export const EPS_YOGUNLUK = 20; // kg/m³

// Standart kütükler: 1230×2000mm, 7 kalınlık
export const STANDART_KUTUKLER: IStraforKutuk[] = [
  { id: 'eps-50',  isim: 'EPS 50mm',  en: 1230, boy: 2000, yukseklik: 50,  adet: 99, renk: '#A5D6A7', birimFiyat: 30 },
  { id: 'eps-100', isim: 'EPS 100mm', en: 1230, boy: 2000, yukseklik: 100, adet: 99, renk: '#FFB74D', birimFiyat: 50 },
  { id: 'eps-150', isim: 'EPS 150mm', en: 1230, boy: 2000, yukseklik: 150, adet: 99, renk: '#4FC3F7', birimFiyat: 70 },
  { id: 'eps-200', isim: 'EPS 200mm', en: 1230, boy: 2000, yukseklik: 200, adet: 99, renk: '#81C784', birimFiyat: 90 },
  { id: 'eps-250', isim: 'EPS 250mm', en: 1230, boy: 2000, yukseklik: 250, adet: 99, renk: '#F06292', birimFiyat: 110 },
  { id: 'eps-300', isim: 'EPS 300mm', en: 1230, boy: 2000, yukseklik: 300, adet: 99, renk: '#D1C4E9', birimFiyat: 130 },
  { id: 'eps-350', isim: 'EPS 350mm', en: 1230, boy: 2000, yukseklik: 350, adet: 99, renk: '#FFD54F', birimFiyat: 150 },
];
